import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  DollarSign, MapPin, Lock, ArrowLeft, LogIn, LogOut, Check,
  ChevronLeft, X, Loader2, AlertTriangle,
} from "lucide-react";
import useKioskManifest from "../hooks/useKioskManifest";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Re-use the same pairing-code key as the Time Clock kiosk — owners
// pair the device ONCE and both kiosks light up.
const KIOSK_CODE_STORAGE_KEY = "salonflow_kiosk_code";

const fmt$ = (n) => `$${(Number(n) || 0).toFixed(2)}`;

// ─── Cash Drawer Kiosk ─────────────────────────────────────────────
// Public, PIN-secured form for staff to submit opening and closing
// cash counts. Companion to /clock — same auth model, same pairing.
export default function CashDrawerKioskPage() {
  useKioskManifest({ manifest: "/manifest-cash-drawer.json", title: "Cash Drawer" });
  const [kioskCode, setKioskCode] = useState(() => localStorage.getItem(KIOSK_CODE_STORAGE_KEY) || "");
  const [paired, setPaired] = useState(false);
  // Wizard stage: pair → location → staff (PIN) → counts → done
  const [stage, setStage] = useState("location"); // location | staff | counts | done
  const [locations, setLocations] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [pin, setPin] = useState("");
  const [drawerType, setDrawerType] = useState("open");
  const [cashInDrawer, setCashInDrawer] = useState("");
  const [cashDropped, setCashDropped] = useState("");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [doneState, setDoneState] = useState(null);

  // Pairing screen first (matches the Time Clock UX)
  const [pairInput, setPairInput] = useState("");
  const [pairingBusy, setPairingBusy] = useState(false);

  const headers = useMemo(() => ({ "X-Kiosk-Code": kioskCode }), [kioskCode]);

  // Validate the existing pairing on first mount; if it doesn't resolve
  // to a tenant the user gets the pair-screen.
  useEffect(() => {
    if (!kioskCode) { setPaired(false); return; }
    axios.get(`${API}/cash-drawer/locations`, { headers })
      .then(r => { setLocations(r.data || []); setPaired(true); })
      .catch(() => { setPaired(false); localStorage.removeItem(KIOSK_CODE_STORAGE_KEY); setKioskCode(""); });
  }, [kioskCode, headers]);

  const submitPair = async (e) => {
    e?.preventDefault();
    const code = pairInput.trim();
    if (code.length < 4) return;
    setPairingBusy(true);
    try {
      // Try the locations endpoint as a "test" call — if 200, code is valid.
      const r = await axios.get(`${API}/cash-drawer/locations`, { headers: { "X-Kiosk-Code": code } });
      localStorage.setItem(KIOSK_CODE_STORAGE_KEY, code);
      setKioskCode(code);
      setLocations(r.data || []);
      setPaired(true);
    } catch {
      alert("Invalid pairing code — ask your owner for a fresh one from Settings → Time Clock.");
    } finally { setPairingBusy(false); }
  };

  // Once a location is picked, pull the staff list (only those with PINs)
  const loadStaff = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cash-drawer/staff`, { headers });
      setStaff(r.data || []);
    } catch { setStaff([]); }
  }, [headers]);
  useEffect(() => { if (paired) loadStaff(); }, [paired, loadStaff]);

  // Whenever the staff/PIN/location/drawer-type combo changes AND we're
  // doing a close, fetch the expected total so the live variance shows.
  useEffect(() => {
    if (stage !== "counts" || drawerType !== "close" || !selectedStaff || pin.length !== 4) {
      setPreview(null); setPreviewError("");
      return;
    }
    let cancelled = false;
    setPreviewError("");
    axios.post(`${API}/cash-drawer/expected`, {
      user_id: selectedStaff.user_id,
      pin,
      location_id: selectedLocation.location_id,
    }, { headers }).then(r => {
      if (!cancelled) setPreview(r.data);
    }).catch(err => {
      if (cancelled) return;
      setPreview(null);
      const d = err.response?.data?.detail || "Couldn't fetch expected total.";
      setPreviewError(d);
    });
    return () => { cancelled = true; };
  }, [stage, drawerType, selectedStaff, selectedLocation, pin, headers]);

  const expectedDrop = useMemo(() => {
    // Live recompute expected-in-drawer once the user enters a drop amount.
    if (!preview) return null;
    const drop = Number(cashDropped) || 0;
    return Math.max(0, +(preview.expected_cash_in_drawer - drop).toFixed(2));
  }, [preview, cashDropped]);

  const variance = useMemo(() => {
    if (drawerType !== "close" || expectedDrop === null || cashInDrawer === "") return null;
    return +(Number(cashInDrawer) - expectedDrop).toFixed(2);
  }, [drawerType, expectedDrop, cashInDrawer]);

  const handlePinDigit = (d) => {
    if (pin.length >= 4) return;
    setPin(prev => (prev + d).slice(0, 4));
  };
  const pinBackspace = () => setPin(p => p.slice(0, -1));

  const submit = async () => {
    if (!selectedStaff || pin.length !== 4 || cashInDrawer === "") {
      alert("Fill in cash in drawer first."); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        user_id: selectedStaff.user_id,
        pin,
        location_id: selectedLocation.location_id,
        drawer_type: drawerType,
        cash_in_drawer: Number(cashInDrawer),
        cash_dropped: Number(cashDropped) || 0,
        note: note.trim(),
      };
      const r = await axios.post(`${API}/cash-drawer/submit`, payload, { headers });
      setDoneState(r.data);
      setStage("done");
    } catch (err) {
      alert(err.response?.data?.detail || "Submit failed.");
    } finally { setSubmitting(false); }
  };

  const reset = () => {
    setSelectedStaff(null); setPin(""); setCashInDrawer(""); setCashDropped("");
    setNote(""); setPreview(null); setPreviewError(""); setDoneState(null);
    setDrawerType("open"); setStage("location");
  };

  // ─── Render ────────────────────────────────────────────────────────
  if (!paired) {
    return (
      <PairScreen pairInput={pairInput} setPairInput={setPairInput}
        submitPair={submitPair} busy={pairingBusy} />
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8E7] flex flex-col" data-testid="cash-drawer-kiosk">
      {/* Top bar */}
      <div className="border-b border-[#E6E4E0] bg-white">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <h1 className="font-['Outfit'] text-lg font-semibold text-slate-900">Cash Drawer</h1>
              <p className="text-[11px] text-slate-500">Submit your opening or closing count</p>
            </div>
          </div>
          {selectedLocation && stage !== "done" && (
            <button onClick={reset} data-testid="cd-restart"
              className="text-xs font-medium text-slate-600 hover:text-slate-900 px-3 h-8 rounded-full hover:bg-slate-100 flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Start over
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 max-w-3xl w-full mx-auto px-6 py-8">
        {/* ── Stage 1: pick location ── */}
        {stage === "location" && (
          <div data-testid="cd-stage-location">
            <h2 className="font-['Outfit'] text-2xl font-light text-slate-900 mb-1">Which location?</h2>
            <p className="text-sm text-slate-500 mb-6">Tap the salon you're counting cash for.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {locations.map(l => (
                <button key={l.location_id} onClick={() => { setSelectedLocation(l); setStage("staff"); }}
                  data-testid={`cd-location-${l.location_id}`}
                  className="bg-white border-2 border-[#E6E4E0] rounded-2xl p-5 text-left hover:border-emerald-400 hover:shadow-sm transition-all">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-slate-900">{l.name}</p>
                    </div>
                  </div>
                </button>
              ))}
              {locations.length === 0 && (
                <p className="text-sm text-slate-400 col-span-2 text-center py-8">No locations found for this kiosk.</p>
              )}
            </div>
          </div>
        )}

        {/* ── Stage 2: pick staff + PIN ── */}
        {stage === "staff" && (
          <div data-testid="cd-stage-staff">
            <button onClick={() => setStage("location")} className="text-xs text-slate-500 hover:text-slate-900 mb-3 inline-flex items-center gap-1">
              <ChevronLeft className="w-3 h-3" /> Change location
            </button>
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{selectedLocation?.name}</p>
            <h2 className="font-['Outfit'] text-2xl font-light text-slate-900 mb-1 mt-1">Who's counting?</h2>
            <p className="text-sm text-slate-500 mb-6">Tap your name, then enter your 4-digit PIN.</p>

            {!selectedStaff ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {staff.map(s => (
                  <button key={s.user_id} onClick={() => setSelectedStaff(s)}
                    data-testid={`cd-staff-${s.user_id}`}
                    className="bg-white border-2 border-[#E6E4E0] rounded-2xl p-4 text-center hover:border-emerald-400 transition-all">
                    <div className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center text-white font-bold text-lg"
                      style={{ backgroundColor: s.color || "#B5A493" }}>
                      {s.name?.[0] || "?"}
                    </div>
                    <p className="text-sm font-semibold text-slate-900 truncate">{s.name}</p>
                    <p className="text-[10px] text-slate-400 capitalize">{s.role}</p>
                  </button>
                ))}
                {staff.length === 0 && (
                  <p className="text-sm text-slate-400 col-span-full text-center py-8">No staff with PINs at this location.</p>
                )}
              </div>
            ) : (
              <div className="bg-white border-2 border-[#E6E4E0] rounded-2xl p-6 max-w-md mx-auto">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                    style={{ backgroundColor: selectedStaff.color || "#B5A493" }}>
                    {selectedStaff.name?.[0] || "?"}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{selectedStaff.name}</p>
                    <p className="text-[11px] text-slate-500">Enter your PIN</p>
                  </div>
                  <button onClick={() => { setSelectedStaff(null); setPin(""); }}
                    className="ml-auto w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex justify-center gap-3 mb-5">
                  {[0,1,2,3].map(i => (
                    <div key={i} className={`w-4 h-4 rounded-full ${pin.length > i ? "bg-slate-900" : "bg-slate-200"}`} />
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {[1,2,3,4,5,6,7,8,9].map(n => (
                    <button key={n} onClick={() => handlePinDigit(String(n))}
                      data-testid={`cd-pin-${n}`}
                      className="h-14 bg-[#FAFAFA] rounded-xl font-['Outfit'] text-2xl font-light hover:bg-slate-100">
                      {n}
                    </button>
                  ))}
                  <div />
                  <button onClick={() => handlePinDigit("0")} data-testid="cd-pin-0"
                    className="h-14 bg-[#FAFAFA] rounded-xl font-['Outfit'] text-2xl font-light hover:bg-slate-100">0</button>
                  <button onClick={pinBackspace}
                    className="h-14 bg-[#FAFAFA] rounded-xl text-slate-500 hover:bg-slate-100 flex items-center justify-center">
                    ⌫
                  </button>
                </div>
                <button onClick={() => setStage("counts")} disabled={pin.length !== 4}
                  data-testid="cd-pin-continue"
                  className="w-full h-12 rounded-xl bg-slate-900 text-white font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-800">
                  Continue
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Stage 3: enter counts ── */}
        {stage === "counts" && (
          <div data-testid="cd-stage-counts" className="max-w-xl mx-auto">
            <button onClick={() => { setStage("staff"); setPin(""); }}
              className="text-xs text-slate-500 hover:text-slate-900 mb-3 inline-flex items-center gap-1">
              <ChevronLeft className="w-3 h-3" /> Back
            </button>
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
              {selectedLocation?.name} · {selectedStaff?.name}
            </p>
            <h2 className="font-['Outfit'] text-2xl font-light text-slate-900 mb-5 mt-1">Cash count</h2>

            {/* Open / Close toggle */}
            <div className="grid grid-cols-2 gap-2 mb-5">
              <button onClick={() => setDrawerType("open")} data-testid="cd-type-open"
                className={`h-14 rounded-xl border-2 font-semibold transition-all flex items-center justify-center gap-2 ${
                  drawerType === "open"
                    ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                }`}>
                <LogIn className="w-4 h-4" /> Opening count
              </button>
              <button onClick={() => setDrawerType("close")} data-testid="cd-type-close"
                className={`h-14 rounded-xl border-2 font-semibold transition-all flex items-center justify-center gap-2 ${
                  drawerType === "close"
                    ? "border-rose-500 bg-rose-50 text-rose-700"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                }`}>
                <LogOut className="w-4 h-4" /> Closing count
              </button>
            </div>

            {/* Cash in drawer */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-3">
              <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
                Cash in drawer (right now)
              </label>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-3xl font-light text-slate-400">$</span>
                <input type="number" inputMode="decimal" step="0.01" min="0"
                  value={cashInDrawer} onChange={e => setCashInDrawer(e.target.value)}
                  placeholder="0.00"
                  data-testid="cd-cash-in-drawer"
                  className="flex-1 h-14 text-3xl font-light bg-[#FAFAFA] rounded-xl px-4 border border-slate-200 focus:border-emerald-400 outline-none tabular-nums" />
              </div>
            </div>

            {/* Close-only: dropped + variance */}
            {drawerType === "close" && (
              <>
                <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-3">
                  <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
                    Cash dropped (taken out of drawer / to safe)
                  </label>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-3xl font-light text-slate-400">$</span>
                    <input type="number" inputMode="decimal" step="0.01" min="0"
                      value={cashDropped} onChange={e => setCashDropped(e.target.value)}
                      placeholder="0.00"
                      data-testid="cd-cash-dropped"
                      className="flex-1 h-14 text-3xl font-light bg-[#FAFAFA] rounded-xl px-4 border border-slate-200 focus:border-emerald-400 outline-none tabular-nums" />
                  </div>
                </div>

                {/* Live expected / variance — only shown after PIN is validated */}
                {previewError && (
                  <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-3 text-sm text-amber-800">{previewError}</div>
                )}
                {preview && (
                  <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-3 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Opening cash</span>
                      <span className="font-semibold text-slate-900 tabular-nums">{fmt$(preview.opening_cash)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">+ Cash sales today ({preview.transaction_count} txn)</span>
                      <span className="font-semibold text-emerald-700 tabular-nums">+ {fmt$(preview.cash_sales_total)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">− Cash dropped</span>
                      <span className="font-semibold text-rose-700 tabular-nums">− {fmt$(Number(cashDropped) || 0)}</span>
                    </div>
                    <div className="border-t border-slate-100 pt-2 mt-1 flex items-center justify-between">
                      <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Expected in drawer</span>
                      <span className="font-['Outfit'] text-2xl font-semibold text-slate-900 tabular-nums">{fmt$(expectedDrop)}</span>
                    </div>
                    {variance !== null && cashInDrawer !== "" && (
                      <div className={`mt-2 p-3 rounded-xl flex items-center justify-between text-sm font-semibold ${
                        variance === 0 ? "bg-emerald-50 text-emerald-700"
                        : variance < 0 ? "bg-rose-50 text-rose-700"
                        : "bg-amber-50 text-amber-700"
                      }`}>
                        <span>{variance === 0 ? "✓ Right on" : variance < 0 ? "Short by" : "Over by"}</span>
                        <span className="tabular-nums">{fmt$(Math.abs(variance))}</span>
                      </div>
                    )}
                  </div>
                )}
                {!preview && !previewError && pin.length === 4 && (
                  <div className="flex items-center gap-2 text-xs text-slate-400 px-2 mb-3">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading today's sales...
                  </div>
                )}
              </>
            )}

            {/* Optional note */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-5">
              <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Note (optional)</label>
              <input value={note} onChange={e => setNote(e.target.value)}
                placeholder="Mention anything unusual..."
                data-testid="cd-note"
                className="mt-2 w-full h-11 bg-[#FAFAFA] rounded-xl px-4 border border-slate-200 focus:border-emerald-400 outline-none text-sm" />
            </div>

            <button onClick={submit} disabled={submitting || cashInDrawer === ""}
              data-testid="cd-submit"
              className="w-full h-14 rounded-xl bg-slate-900 text-white font-semibold text-base disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-800 flex items-center justify-center gap-2">
              {submitting ? "Saving..." : drawerType === "open" ? "Submit opening count" : "Submit closing count"}
            </button>
          </div>
        )}

        {/* ── Stage 4: confirmation ── */}
        {stage === "done" && doneState && (
          <div data-testid="cd-stage-done" className="max-w-md mx-auto text-center pt-8">
            <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-emerald-700" />
            </div>
            <h2 className="font-['Outfit'] text-2xl font-light text-slate-900">
              {doneState.drawer_type === "open" ? "Opening count saved" : "Closing count saved"}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {doneState.user_name} · {doneState.location_name}
            </p>
            <div className="bg-white border border-slate-200 rounded-2xl p-5 mt-6 text-left space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Cash in drawer</span>
                <span className="font-semibold tabular-nums">{fmt$(doneState.cash_in_drawer)}</span>
              </div>
              {doneState.drawer_type === "close" && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Dropped to safe</span>
                    <span className="font-semibold tabular-nums">{fmt$(doneState.cash_dropped)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Expected</span>
                    <span className="font-semibold tabular-nums">{fmt$(doneState.expected_cash)}</span>
                  </div>
                  <div className={`flex justify-between text-sm border-t border-slate-100 pt-2 font-semibold ${
                    doneState.variance === 0 ? "text-emerald-700"
                    : (doneState.variance || 0) < 0 ? "text-rose-700" : "text-amber-700"
                  }`}>
                    <span>{doneState.variance === 0 ? "Right on" : (doneState.variance || 0) < 0 ? "Short" : "Over"}</span>
                    <span className="tabular-nums">{fmt$(Math.abs(doneState.variance || 0))}</span>
                  </div>
                </>
              )}
            </div>
            <button onClick={reset} data-testid="cd-done-restart"
              className="mt-6 w-full h-12 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800">
              Submit another count
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


function PairScreen({ pairInput, setPairInput, submitPair, busy }) {
  return (
    <div className="min-h-screen bg-[#FFF8E7] flex items-center justify-center p-6">
      <div className="bg-white border border-[#E6E4E0] rounded-2xl p-8 max-w-md w-full text-center" data-testid="cd-pair-screen">
        <div className="w-14 h-14 rounded-full bg-emerald-50 mx-auto flex items-center justify-center mb-4">
          <Lock className="w-7 h-7 text-emerald-700" />
        </div>
        <h1 className="font-['Outfit'] text-2xl font-light text-slate-900">Pair this device</h1>
        <p className="text-sm text-slate-500 mt-1">Enter the kiosk code from <span className="font-medium">Settings → Time Clock → Kiosk Code</span>.</p>
        <form onSubmit={submitPair} className="mt-6 space-y-3">
          <input value={pairInput} onChange={e => setPairInput(e.target.value.replace(/[^A-Z0-9]/gi, "").toUpperCase())}
            maxLength={12} autoFocus
            placeholder="e.g. K7AB39"
            data-testid="cd-pair-input"
            className="w-full h-14 text-center font-['Outfit'] text-2xl tracking-[0.4em] uppercase bg-[#FAFAFA] rounded-xl px-4 border border-slate-200 focus:border-emerald-400 outline-none" />
          <button type="submit" disabled={busy || pairInput.length < 4}
            data-testid="cd-pair-submit"
            className="w-full h-12 rounded-xl bg-slate-900 text-white font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-800">
            {busy ? "Pairing..." : "Pair device"}
          </button>
        </form>
      </div>
    </div>
  );
}
